import React, { useState, useEffect } from 'react';
import { 
  FileCheck, 
  Printer, 
  CheckCircle2, 
  Clock, 
  AlertCircle, 
  FileText,
  Filter,
  FileDown
} from 'lucide-react';
import { AosrAct } from '../../types';
import { generateAosrHtml } from '../../utils/exportAosr';
import { exportAosrToDocx } from '../../utils/exportDocx';

export const ActsRegistry: React.FC = () => {
  const [acts, setActs] = useState<AosrAct[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');

  const fetchActs = () => {
    setLoading(true);
    const url = statusFilter ? `/api/v1/pto/acts?status=${statusFilter}` : '/api/v1/pto/acts';
    fetch(url)
      .then(r => r.json())
      .then(d => {
        if (d.success) setActs(d.data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchActs();
  }, [statusFilter]);

  const updateStatus = async (actId: string, newStatus: string) => {
    try {
      const res = await fetch(`/api/v1/pto/acts/${actId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      const data = await res.json();
      if (data.success) {
        fetchActs();
      }
    } catch (err) {
      console.error(err);
      alert('Ошибка при обновлении статуса.');
    }
  };

  const printAct = (act: AosrAct) => {
    const html = generateAosrHtml(act, { name: act.project_name || 'ЖК «Северная Звезда»' }, []);
    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'signed':
        return (
          <span className="flex items-center gap-1 text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-200 text-xs font-bold">
            <CheckCircle2 className="w-3.5 h-3.5" /> Подписан Технадзором
          </span>
        );
      case 'submitted':
        return (
          <span className="flex items-center gap-1 text-blue-700 bg-blue-50 px-2.5 py-1 rounded-lg border border-blue-200 text-xs font-bold">
            <Clock className="w-3.5 h-3.5" /> На согласовании
          </span>
        );
      case 'remarks':
        return (
          <span className="flex items-center gap-1 text-rose-700 bg-rose-50 px-2.5 py-1 rounded-lg border border-rose-200 text-xs font-bold">
            <AlertCircle className="w-3.5 h-3.5" /> Есть замечания
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 text-amber-700 bg-amber-50 px-2.5 py-1 rounded-lg border border-amber-200 text-xs font-bold">
            <FileText className="w-3.5 h-3.5" /> Черновик ПТО
          </span>
        );
    }
  };

  return (
    <div className="space-y-4">
      {/* Header & Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-4 rounded-2xl border border-slate-200/90 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]">
        <div>
          <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
            <span className="p-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-xl">
              <FileCheck className="w-5 h-5" />
            </span>
            <span>Реестр исполнительной документации (Акты АОСР)</span>
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Юридический реестр актов освидетельствования скрытых работ (РД-11-02-2006) с привязкой сертификатов
          </p>
        </div>

        <div className="flex items-center gap-2 text-xs">
          <Filter className="w-4 h-4 text-slate-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:border-blue-500 font-semibold"
          >
            <option value="">Все статусы актов</option>
            <option value="draft">Черновики ПТО</option>
            <option value="submitted">На согласовании</option>
            <option value="signed">Подписано Технадзором</option>
            <option value="remarks">Замечания</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-slate-400 text-sm">Загрузка реестра АОСР...</div>
      ) : acts.length === 0 ? (
        <div className="p-8 text-center bg-white rounded-2xl border border-slate-200 text-slate-500 text-sm">
          По выбранным критериям актов АОСР не найдено.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {acts.map((act) => (
            <div
              key={act.id}
              className="bg-white border border-slate-200/90 hover:border-slate-300 p-4 rounded-2xl space-y-3 shadow-sm transition"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm font-black text-slate-900 bg-slate-100 px-2.5 py-1 rounded-xl border border-slate-200">
                    Акт № {act.actNumber || act.act_number}
                  </span>
                  <div>
                    <h3 className="font-bold text-slate-900 text-sm">{act.workName || act.work_name}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">{act.workLocation || act.work_location}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 self-end sm:self-center">
                  {getStatusBadge(act.status)}
                  <span className="text-xs font-bold text-amber-700 bg-amber-50 px-2.5 py-1 rounded-lg border border-amber-200">
                    {act.volume} {act.unit}
                  </span>
                </div>
              </div>

              {/* Details & Dates */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-xs text-slate-600 bg-slate-50 p-3 rounded-xl border border-slate-100">
                <div>
                  <span className="text-slate-400 block text-[10px]">Период выполнения:</span>
                  <span className="font-medium text-slate-800">{act.startDate || act.start_date} — {act.endDate || act.end_date}</span>
                </div>

                <div>
                  <span className="text-slate-400 block text-[10px]">Материалы / Паспорт:</span>
                  <span className="font-mono text-slate-700">{act.materialsUsed || act.materials_used || 'Кабель ВВГнг-LS (ГОСТ)'}</span>
                </div>

                <div>
                  <span className="text-slate-400 block text-[10px]">Сертификат соответствия:</span>
                  <span className="font-semibold text-emerald-700">{act.certificateNumber || act.certificate_number || '№ ЕАЭС RU C-RU.АБ05'}</span>
                </div>

                <div>
                  <span className="text-slate-400 block text-[10px]">Технадзор Заказчика:</span>
                  <span className="font-medium text-slate-800">{act.repSupervision || act.rep_supervision || 'Ковалев В.М.'}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap items-center justify-between gap-2 pt-1 text-xs">
                <div className="flex items-center gap-2">
                  {act.status !== 'signed' && (
                    <button
                      onClick={() => updateStatus(act.id, 'signed')}
                      className="bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 px-3 py-1.5 rounded-xl font-bold transition active:scale-95"
                    >
                      ✓ Утвердить (Подпись Технадзора)
                    </button>
                  )}
                  {act.status === 'draft' && (
                    <button
                      onClick={() => updateStatus(act.id, 'submitted')}
                      className="bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 px-3 py-1.5 rounded-xl font-bold transition active:scale-95"
                    >
                      На согласование
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => printAct(act)}
                    className="flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 px-3 py-1.5 rounded-xl font-bold border border-slate-200 transition active:scale-95"
                  >
                    <Printer className="w-3.5 h-3.5" />
                    <span>Печать АОСР (HTML)</span>
                  </button>

                  <button
                    onClick={() => exportAosrToDocx(act, { name: act.project_name || 'ЖК «Северная Звезда»' }, [])}
                    className="flex items-center gap-1.5 bg-amber-50 hover:bg-amber-100 text-amber-800 px-3 py-1.5 rounded-xl font-bold border border-amber-200 transition active:scale-95"
                  >
                    <FileDown className="w-3.5 h-3.5" />
                    <span>Скачать DOCX</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
